import PropTypes from 'prop-types';
import React from 'react';
import styled, { withTheme } from 'styled-components';
import FocusTrap from 'focus-trap-react';
import { RemoveScroll } from 'react-remove-scroll';
import { hook, withValue } from '@g4/automation-hooks';
import Box from '@g4/boneyard/lib/Box';
import Text from '@g4/boneyard/lib/Text';

import closeIconDefault from '../../../images/ic-close-blue.svg';

function stopBubble(evt) {
  evt.stopPropagation();
}

const Overlay = styled(Box)`
  height: 100%;
  width: 100%;
`;

export const Wrapper = styled.div`
  position: relative;
  box-shadow: 0 5px 20px 0 rgba(96, 125, 139, 0.5),
    0 5px 5px 0 rgba(120, 144, 156, 0.2);
  border-radius: 10px;
  background-color: white;
  max-width: ${props =>
    props.customWidth ? props.customWidth : props.theme.layout.constrain}px;
  display: flex;
  flex-direction: column;
  white-space: normal;
  max-height: 100%;

  ${props =>
    props.fullWidth &&
    `
      width: 100%;
    `}

  ${props =>
    props.withDefaultPadding &&
    `
    padding: 30px 10px;
    ${props => props.theme.query.md} {
      padding: 30px;
    }
  `}
`;

const CloseIcon = styled.button`
  position: absolute;
  z-index: 1;
  top: 10px;
  right: 10px;
  height: 24px;
  width: 24px;
  ${props => props.theme.query.lg} {
    top: 32px;
    right: 32px;
  }
`;

const ModalHeader = styled(Box)`
  text-align: left;
  ${props => props.theme.query.md} {
    text-align: ${props => props.titleAlign};
  }
`;

const Popup = ({
  title,
  titleAlign,
  automationHook,
  open,
  onRequestClose,
  theme,
  children,
  withDefaultPadding = true,
  closeIconPath,
  fullWidth = false,
  className,
  customWidth,
}) => {
  const handleKeyDown = e => {
    if (e.keyCode === 27) {
      onRequestClose();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <div className={className} onClick={onRequestClose}>
        <Overlay
          position="fixed"
          top={0}
          left={0}
          zIndex={1000}
          backgroundColor="rgba(51, 51, 51, 0.8)"
        >
          <FocusTrap
            active={true}
            focusTrapOptions={{
              clickOutsideDeactivates: true,
            }}
          >
            {/* This <div/> exists to allow FocusTrap to apply a ref. Box doesn't work, needs to be a <div> */}
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '15px',
              }}
            >
              <RemoveScroll forwardProps>
                <Wrapper
                  onKeyDown={handleKeyDown}
                  {...hook(automationHook)}
                  role="status"
                  onClick={stopBubble}
                  withDefaultPadding={withDefaultPadding}
                  fullWidth={fullWidth}
                  customWidth={customWidth}
                >
                  {title && (
                    <ModalHeader pb={6} w="100%" titleAlign={titleAlign}>
                      <Text
                        size={22}
                        weight={500}
                        color={theme.colors.common.peacockBlue}
                        automationHook={withValue(automationHook, 'title')}
                      >
                        {title}
                      </Text>

                      <CloseIcon
                        onClick={onRequestClose}
                        {...hook(automationHook, 'close')}
                      >
                        <img alt="closeIcon" src={closeIconPath} />
                      </CloseIcon>
                    </ModalHeader>
                  )}
                  <Box
                    position="relative"
                    overflow="auto"
                    automationHook={withValue(automationHook, 'content')}
                  >
                    <>
                      {!title && (
                        <CloseIcon
                          onClick={onRequestClose}
                          {...hook(automationHook, 'close')}
                        >
                          <img alt="closeIcon" src={closeIconPath} />
                        </CloseIcon>
                      )}
                      {children}
                    </>
                  </Box>
                </Wrapper>
              </RemoveScroll>
            </div>
          </FocusTrap>
        </Overlay>
      </div>
    </>
  );
};

Popup.propTypes = {
  title: PropTypes.string,
  titleAlign: PropTypes.oneOf(['left', 'center']),
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  /** Determines if modal is displaying */
  open: PropTypes.bool,
  /** all component children nodes */
  onRequestClose: PropTypes.func,
  /** use dismissible on mobile viewport */
  theme: PropTypes.object.isRequired,
  children: PropTypes.node,
  withDefaultPadding: PropTypes.bool,
  closeIconPath: PropTypes.string,
  fullWidth: PropTypes.bool,
  className: PropTypes.object,
  customWidth: PropTypes.number,
};

Popup.defaultProps = {
  title: '',
  titleAlign: 'center',
  onRequestClose: Function.prototype,
  open: false,
  withDismissible: false,
  noDismissiblePadding: false,
  closeIconPath: closeIconDefault,
};

export default withTheme(Popup);
